import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import styles from './NovelStatusTool.module.css';

const STATUS_FIELDS = [
  { key: 'has_content', label: '中文原文' },
  { key: 'has_content_trans', label: '英文翻译' },
  { key: 'has_ai_notes', label: 'AI 笔记' },
  { key: 'oss_en_mp3', label: 'en.mp3' },
  { key: 'oss_cn_mp3', label: 'cn.mp3' },
  { key: 'oss_en_cn_mp3', label: 'en_cn.mp3' },
  { key: 'oss_cn_en_mp3', label: 'cn_en.mp3' },
];

const SUMMARY_KEYS = [
  { countKey: 'count_has_content', label: '中文' },
  { countKey: 'count_has_content_trans', label: '翻译' },
  { countKey: 'count_has_ai_notes', label: '笔记' },
  { countKey: 'count_oss_en_mp3', label: 'en.mp3' },
  { countKey: 'count_oss_cn_mp3', label: 'cn.mp3' },
  { countKey: 'count_oss_en_cn_mp3', label: 'en_cn' },
  { countKey: 'count_oss_cn_en_mp3', label: 'cn_en' },
];

function NovelStatusTool() {
  const [volumes, setVolumes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 用于筛选小说的列表
  const [novels, setNovels] = useState([]);
  const [selectedNovelId, setSelectedNovelId] = useState('');

  // 展开/折叠的卷 IDs
  const [expandedVolumes, setExpandedVolumes] = useState(new Set());

  // 加载小说列表（仅名称，不加载状态数据）
  const loadNovels = useCallback(async () => {
    try {
      const { data } = await api.get('/novels/', { params: { limit: 500 } });
      const list = Array.isArray(data) ? data : [];
      setNovels(list);
    } catch {
      // 静默失败
    }
  }, []);

  useEffect(() => {
    loadNovels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 加载状态数据
  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (selectedNovelId) {
        params.novel_id = selectedNovelId;
      }
      const { data } = await api.get('/novel-status/volumes', { params });
      const vols = data?.volumes || [];
      setVolumes(vols);
      // 默认全部展开
      setExpandedVolumes(new Set(vols.map((v) => v.id)));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || '获取状态失败';
      setError(detail);
      setVolumes([]);
    } finally {
      setLoading(false);
    }
  }, [selectedNovelId]);

  // 切换小说时清空旧数据，等待用户手动点击加载
  useEffect(() => {
    setVolumes([]);
    setError('');
  }, [selectedNovelId]);

  const toggleVolume = useCallback((volId) => {
    setExpandedVolumes((prev) => {
      const next = new Set(prev);
      if (next.has(volId)) {
        next.delete(volId);
      } else {
        next.add(volId);
      }
      return next;
    });
  }, []);

  // 全局汇总
  const overall = useMemo(() => {
    let total = 0;
    const sums = {};
    SUMMARY_KEYS.forEach((s) => {
      sums[s.countKey] = 0;
    });
    for (const vol of volumes) {
      total += vol.total_chapters || 0;
      SUMMARY_KEYS.forEach((s) => {
        sums[s.countKey] += vol[s.countKey] || 0;
      });
    }
    return { total, sums };
  }, [volumes]);

  const getSummaryClass = (count, total) => {
    if (total === 0) return '';
    if (count === total) return styles.summaryFull;
    if (count === 0) return styles.summaryEmpty;
    return styles.summaryPartial;
  };

  return (
    <div className={styles.statusPage}>
      {/* Header */}
      <div className={styles.headerCard}>
        <h2 className={styles.headerTitle}>小说状态检查</h2>
        <p className={styles.headerHint}>
          按卷列出每章的数据库字段（中文、翻译、笔记）和 OSS 音频文件状态
        </p>

        <div className={styles.filterRow}>
          <select
            className={styles.select}
            value={selectedNovelId}
            onChange={(e) => setSelectedNovelId(e.target.value)}
            disabled={loading}
          >
            <option value="">全部小说</option>
            {novels.map((n) => (
              <option key={n.id} value={n.id}>
                {n.title} — {n.author}
              </option>
            ))}
          </select>

          <button
            className={styles.refreshBtn}
            onClick={loadStatus}
            disabled={loading || !selectedNovelId}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
              {volumes.length > 0 ? 'refresh' : 'play_arrow'}
            </span>
            {loading ? '加载中…' : volumes.length > 0 ? '刷新' : '加载'}
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}
      </div>

      {/* Overall Stats */}
      {volumes.length > 0 && (
        <div className={styles.overallStats}>
          <h3 className={styles.overallTitle}>
            总览：{volumes.length} 卷 · {overall.total} 章
          </h3>
          <div className={styles.overallGrid}>
            {SUMMARY_KEYS.map((s) => {
              const count = overall.sums[s.countKey];
              const isFull = count === overall.total && overall.total > 0;
              return (
                <div
                  key={s.countKey}
                  className={`${styles.statCard} ${isFull ? styles.statFull : ''}`}
                >
                  <span className={styles.statValue}>
                    {count}/{overall.total}
                  </span>
                  <span className={styles.statLabel}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && <div className={styles.loading}>正在加载状态数据…</div>}

      {/* Empty */}
      {!loading && volumes.length === 0 && !error && (
        <div className={styles.emptyState}>
          {selectedNovelId ? '请点击「加载」按钮查看状态' : '请先选择一本小说'}
        </div>
      )}

      {/* Volume Cards */}
      {volumes.map((vol) => {
        const isExpanded = expandedVolumes.has(vol.id);
        return (
          <div key={vol.id} className={styles.volumeCard}>
            <div className={styles.volumeHeader} onClick={() => toggleVolume(vol.id)}>
              <div className={styles.volumeHeaderLeft}>
                <h3 className={styles.volumeTitle}>
                  {vol.title}
                </h3>
                {vol.novel_title && (
                  <span className={styles.volumeNovel}>{vol.novel_title}</span>
                )}
              </div>
              <span
                className={`material-symbols-rounded ${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}
              >
                expand_more
              </span>
            </div>

            {/* Summary pills */}
            <div className={styles.summaryBar}>
              {SUMMARY_KEYS.map((s) => {
                const count = vol[s.countKey] || 0;
                const total = vol.total_chapters || 0;
                return (
                  <span
                    key={s.countKey}
                    className={`${styles.summaryItem} ${getSummaryClass(count, total)}`}
                  >
                    {s.label} {count}/{total}
                  </span>
                );
              })}
            </div>

            {/* Table */}
            {isExpanded && (
              <div className={styles.tableWrap}>
                <table className={styles.statusTable}>
                  <thead>
                    <tr>
                      <th>章节</th>
                      {STATUS_FIELDS.map((f) => (
                        <th key={f.key}>{f.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {vol.chapters.map((ch) => (
                      <tr key={ch.id}>
                        <td className={styles.chapterName}>
                          <span className={styles.chapterId}>#{ch.id}</span>
                          {ch.title}
                        </td>
                        {STATUS_FIELDS.map((f) => (
                          <td key={f.key}>
                            <span
                              className={`material-symbols-rounded ${
                                ch[f.key] ? styles.iconYes : styles.iconNo
                              }`}
                            >
                              {ch[f.key] ? 'check_circle' : 'cancel'}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default NovelStatusTool;

